import {
  useErrorActionHandlerUseCase,
  useErrorService,
  useGlobalWindow,
  useLoggerService,
  useProjectUserAuth,
} from '@/di/di.root-container.hooks';
import {ErrorAction} from '@/services/ErrorService/ErrorService.constants';

import {FC, useEffect, useState} from 'react';

import {HttpError} from '@specials/project-user';

import {FrontendError} from '@/services/ErrorService/FrontendError';
import {UIErrorComponent} from '@/ui/common/AppError/constants';

import {ErrorButton, ErrorCode, ErrorRequestId, ErrorSubTitle, ErrorTimestamp, ErrorTitle} from '../components';

import s from './AppErrorFullScreen.module.css';

export interface FullScreenErrorProps {
  error: FrontendError | HttpError | unknown;
}

export const AppErrorFullScreen: FC<FullScreenErrorProps> = ({error}) => {
  const errorService = useErrorService();
  const errorActionHandlerUseCase = useErrorActionHandlerUseCase();
  const loggerService = useLoggerService();

  const [isLoading, setIsLoading] = useState(false);

  // TODO: внутри метода вызывается loggerService
  // необходимо подумать над реализацией которая гарантирует одиночный вызов
  // логирования ошибки, повторный вызов в теории может быть связан с вызовом
  // метода в UI (ререндер)
  const errorData = errorService.buildErrorData(error);

  const {title, buttonText, text, action} = errorData.resources;

  const {errorCode, requestId, timestamp} = errorData.breadcrumbs;

  const onButtonClick = (): void => {
    if (!action) return;

    setIsLoading(true);

    try {
      void errorActionHandlerUseCase.handleErrorAction(action);
    } finally {
      setIsLoading(false);
    }
  };

  useLogoutOnBeforeUnload(action);

  useEffect(() => {
    if (typeof error !== 'undefined' && error !== null) {
      loggerService.logDisplayErrorUIComponent(error, errorData.breadcrumbs, UIErrorComponent.Fullscreen);
    }
  }, []);

  return (
    <>
      <ErrorTitle title={title} />
      <ErrorSubTitle text={text} />

      <div className={s.content}>
        <ErrorCode errorCode={errorCode} requestId={requestId} error={error} />
        <ErrorRequestId requestId={requestId} error={error} />
        <ErrorTimestamp timestamp={timestamp} />
      </div>
      {/* так как в ресурсах мы не добавляем buttonText в объект если он не нужен, а в компоненте prop required */}
      <ErrorButton isDisabled={isLoading} buttonText={buttonText || ''} onClick={onButtonClick} />
    </>
  );
};

const useLogoutOnBeforeUnload = (action?: ErrorAction): void => {
  const projectUserAuthService = useProjectUserAuth();
  const globalWindow = useGlobalWindow();

  // Хук подписки на событие beforeunload для вызова метода разлогина.
  // Будет работать, только если action === ErrorAction.Logout,
  // в противном случае нам не нужно подписываться на событие и разлогинивать пользователя.
  useEffect(() => {
    if (action !== ErrorAction.Logout) return;

    const logout = (): Promise<void> => projectUserAuthService.logout();
    // При перезагрузке страницы, закрытии браузера или при использовании кнопок
    // навигации браузера возвращаемый callback хука useEffect не отработает
    // и не вызовет логаут пользователя. Однако отработает событие beforeunload,
    // которое произведет разлогин пользователя
    // @See: https://jira.tcsbank.ru/browse/SP-8244
    globalWindow.addEventListener('beforeunload', logout);

    return () => {
      void logout();
      globalWindow.removeEventListener('beforeunload', logout);
    };
  }, []);
};
